import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { IWizardStepProps } from './MappingWizardStep1';
import getMappingToolStore, { createLineItemFromMappingAndInput, EQuantityTypes } from '../data/MappingToolStore';
import { wizardTitles } from './MappingWizardLeftBar';
import MappingUtils, { getMappingStore, ICandidatesNoFilter } from '../utils/MappingUtils';
import { logDebug, logError, logWarning } from '../../../../shared/common/logger';
import { IDatabaseLineItem, LineItemPreviewPanel } from '../../LineItemDBCreationToolPage';
import { arrayUnion, collection, CollectionReference, doc, getFirestore, setDoc } from 'firebase/firestore';
import { useFirestore, useFirestoreCollectionData } from 'reactfire';
import SharedUtils, { noop, unused } from '../../../../shared/common/SharedUtils';
import { useState } from 'react';
import { allowedUnits, IHeatmapData } from '../../../../shared/common/Azure';
import PrivateUtils, { localLargeStorage } from '../../../../utils/PrivateUtils';
import RestAPI from '../../../../utils/restAPI';
import { toast } from 'react-toastify';
import { MD5 } from 'crypto-js';

import { EExtraVariableValues, VariableMapping } from '../../../../shared/common/EstimateEngine/EstimateEngineTypes';
import { selectablesMap } from '../../../../shared/common/EstimateEngine/TicSelectables';

interface IOpenAIForCleanResult {
	circled: string[],
	all_text: string[],
	maybe_text: string[]
}

interface ILineItemResult {
	lineItem: IDatabaseLineItem;
	createdLineItem?: IMappingData;
}
interface IRenderingArrayItem {
	cleanInput: string;
	parameterizedInput: string;
	minConfidence: number;
	maxConfidence: number;
	candidateWithoutFilter: ICandidatesNoFilter;
	data: IHeatmapData;
	openAIResults?: IOpenAIForCleanResult;
	metaDataId: string;
	lineItems: ILineItemResult[];
	correct: boolean;
	dataIndex: number;
}
type TCleanInputFunction = (
	cleanInput: string,
	key: string,
	fileId: string,
	region64: string,
	candidateWithoutFilter: ICandidatesNoFilter,
	metaDataId: string,
	selectOption?: string[],
	badScans?: string[],
	allowedMaybeText?: string[],
) => Promise<{ openAICleanInput: string, openAIResults?: IOpenAIForCleanResult }>;

interface IMappingData {
	openAIResults?: IOpenAIForCleanResult,
	leftWords: {
		mapped: string,
		word: string
	}[],
	rightWords: {
		mapped: string,
		word: string
	}[],
	mappingCount: number,
	foundMappings: number,
	text: string,
	quantity: number,
	rightWordsMapping: {
		mapping: VariableMapping,
		mapped: string,
		word: string
	}[],
	leftWordsMapping: {
		mapping: VariableMapping,
		mapped: string,
		word: string
	}[],
	selectedValueFromOpenAI?: string,
	usedRnRValue?: string
}
export const toastNotify = (text: string, error?: boolean) => {
	(error ? toast.error : toast.success)(
		<div className='flex items-center'>
			{/* <img src='/assets/ui/success.png' alt='Success' className='w-12 h-12' /> */}
			<div className='mr-4 ml-4 text-gray-900 pr-16'>
				{text}
			</div>
		</div>
	)
};



async function saveMeta(metaDataId: string, key: string, value: any, metaDataPath: string, metaDataSubPath: string, item?: IRenderingArrayItem) {
	getMappingStore().loadingData = true;
	try {
		const firestore = getFirestore();

		try {
			if (key === 'createTestData') {
				const { q1, m1, q2, m2 } = await createTestDataTable(value, item, metaDataId);
			}
		} catch (err) {
			logError('saveMeta - saving to new_TestDataTable', err);
		}



		const metaDocRef = doc(firestore, metaDataSubPath);
		await setDoc(metaDocRef, { hasSubCollection: true });
		const docRef = doc(firestore, metaDataPath, metaDataId);
		logDebug('saveMeta', metaDataPath, metaDataId);
		const dataToSave = { [key]: value };
		if (key === 'approved' && value) {
			const { q1, m1, q2, m2 } = await createTestDataTable(value, item, metaDataId);
			dataToSave['approvedData'] = [{
				text: item!.lineItems.map(li => li.createdLineItem?.text ?? '').filter(t => t),
				q1: q1?.mapped || '', q2: q2?.mapped || '', m1: m1?.mapped || '', m2: m2?.mapped || '',
			}]
		}

		await setDoc(docRef, dataToSave, { merge: true });
	} catch (err) {
		logError('saveMeta', err);
	}
	getMappingStore().loadingData = false;
}

async function createTestDataTable(value: any, item: IRenderingArrayItem | undefined, metaDataId: string) {
	const firestore = getFirestore();
	if (value && item && item.lineItems.length && item.lineItems[0].createdLineItem) {
		const { leftFiltered, rightFiltered, circledText, q1, m1, q2, m2 } = createTestDataForSaving(item)!;

		const fi = metaDataId.indexOf('_');
		const key = metaDataId.substring(fi + 1);
		const testDataTableSub = SharedUtils.getDbPath(`tools/new_TestDataTable/`);
		const firestoreToolsDocRef = doc(firestore, testDataTableSub);
		await setDoc(firestoreToolsDocRef, {
			[key]: arrayUnion({
				input: [leftFiltered.join(' ').toLowerCase(), rightFiltered.join(' ').toLowerCase(), circledText.toLowerCase()],
				// output: item.lineItems.map((li) => li.createdLineItem?.text)
				output: [{
					text: item!.lineItems.map(li => li.createdLineItem?.text ?? '').filter(t => t),
					q1: q1?.mapped || '', q2: q2?.mapped || '', m1: m1?.mapped || '', m2: m2?.mapped || '',
				}]
			})
		}, { merge: true });
		return { q1, m1, q2, m2 };
	}
	return { q1: {}, m1: {}, q2: {}, m2: {} };
}

function createTestDataForSaving(item: IRenderingArrayItem) {
	let q1: any = {}, m1: any = {};
	let q2: any = {}, m2: any = {};
	if (!item.lineItems[0].createdLineItem) {
		return undefined;
	}
	const rightWords = item.lineItems[0].createdLineItem!.rightWords || [];
	const circled = item.lineItems[0].createdLineItem!.openAIResults?.circled || [];
	const leftWords = item.lineItems[0].createdLineItem!.leftWords || [];
	let leftFiltered: any[] = leftWords.filter((l) => !l.word.includes('(') && !l.word.includes(')') && !rightWords.find((r) => r.word === l.word) && !circled.includes(l.word));
	leftFiltered = leftFiltered.filter((w) => !w.word.includes('{') && !w.mapped.includes('{')).map((w) => w.word);
	let rightFiltered = rightWords.filter((w) => !w.word.includes('{') && !w.mapped.includes('{')).map((w) => w.word);

	const searchArray = [...item.lineItems[0].createdLineItem!.leftWords, ...item.lineItems[0].createdLineItem!.rightWords].filter((v, i, a) => a.findIndex(t => (t.mapped === v.mapped && t.word === v.word)) === i);
	q1 = searchArray.find(s => s.word === '{Q}');
	m1 = searchArray.find(s => s.word === '{M}');
	if (q1 && m1) {
		q2 = searchArray.find(s => s.word === '{Q}' && s !== q1);
		m2 = searchArray.find(s => s.word === '{M}' && s !== m1);
		if (!q2 && m2) q2 = q1;
	}
	const circledText = circled.join(',');
	let cleanInput = item.cleanInput.split(' ');
	[...leftFiltered, ...rightFiltered, ...circled].forEach((word) => {
		const index = cleanInput.indexOf(word.toUpperCase());
		if (index !== -1) {
			cleanInput.splice(index, 1);
		}
	});
	let cleanInputText = cleanInput.join(' ').trim();
	if (cleanInputText) {
		leftFiltered.push(cleanInputText);
	}
	return { leftFiltered: leftFiltered as string[], rightFiltered, circledText, q1, m1, q2, m2 };
}

async function saveTommyCorrectionText(metaDataId: string, correctionText: string, metaDataPath: string) {
	getMappingStore().loadingData = true;
	try {
		const firestore = getFirestore();
		const docRef = doc(firestore, metaDataPath, metaDataId);
		await setDoc(docRef, { correctionText }, { merge: true });
	} catch (err) {

	}
	getMappingStore().loadingData = false;
}
async function saveAskTommyText(metaDataId: string, askTommyText: string, metaDataPath: string) {
	getMappingStore().loadingData = true;
	try {
		const firestore = getFirestore();
		const docRef = doc(firestore, metaDataPath, metaDataId);
		await setDoc(docRef, { askTommyText }, { merge: true });
	} catch (err) {

	}
	getMappingStore().loadingData = false;
}
async function saveAddNoteText(metaDataId: string, addNoteText: string, metaDataPath: string) {
	getMappingStore().loadingData = true;
	try {
		const firestore = getFirestore();
		const docRef = doc(firestore, metaDataPath, metaDataId);
		await setDoc(docRef, { addNoteText }, { merge: true });
	} catch (err) {

	}
	getMappingStore().loadingData = false;
}

const MappingWizardStep4: React.FC<IWizardStepProps> = observer(({
	onCancel,
	onNext,
	onBack
}) => {
	const mappingStore = getMappingStore();
	const mappingToolStore = getMappingToolStore();
	const selectedTupple = useRef(mappingStore.templateData!.rows![mappingToolStore.selectedTuple[0]].cellTuples[mappingToolStore.selectedTuple[1]]!);
	const tuppleId = selectedTupple.current.meta?.id!;
	const metaDataSubPath = SharedUtils.getDbPath(`tools/test/${tuppleId}/metadata`);
	const metaDataPath = SharedUtils.getDbPath(`tools/test/${tuppleId}/metadata/inputData`);
	const openAIPath = SharedUtils.getDbPath(`tools/test/${tuppleId}/metadata/openAIQueries`);
	const firestoreToolsCollectionRef = collection(useFirestore(), metaDataPath) as CollectionReference<any>;
	const lineItemsDataFromDataBase = useFirestoreCollectionData<any>(firestoreToolsCollectionRef, { idField: 'metaDataId' });
	const openAIToolsCollectionRef = collection(useFirestore(), openAIPath) as CollectionReference<any>;
	const openAIQueriesFromDataBase = useFirestoreCollectionData<any>(openAIToolsCollectionRef, { idField: 'metaDataId' });
	const [currentFilter, setCurrentFilter] = useState('');
	// const [showDebugJSON, setShowDebugJSON] = useState(false);
	const [showAllMap, setShowAllMap] = useState<{ [key: number]: boolean }>({});
	const [showDebugJSONMap, setShowDebugJSONMap] = useState<{ [key: number]: boolean }>({});
	const [showHidden, setShowHidden] = useState(false);
	const [showHiddenOnly, setShowHiddenOnly] = useState(false);
	const [showUnhandledOnly, setShowUnhandledOnly] = useState(false);
	const [totalItems, setTotalItems] = useState(0);
	const [correctItems, setCorrectItems] = useState(0);
	const [batchedRenderingArray, setBatchedRenderingArray] = useState<IRenderingArrayItem[][]>([]);
	const [useOpenAIForCleaning, setUseOpenAIForCleaning] = useState(true);
	const [processingDataWithOpenAI, setProcessingDataWithOpenAI] = useState(false);
	const [processingDataWithOpenAICounter, setProcessingDataWithOpenAICounter] = useState(0);
	const setRenderingArrayDataLastFilter = useRef('--');
	const setRenderingArrayDataCounter = useRef(0);
	const forceRenderingArrayDataCompute = useRef(0);
	const [checkBoxList, setCheckBoxList] = useState([
		['Scan Error (hide)', 'hide'],
		['Should Approve?', 'shouldApprove'],
		['Tommy Approved', 'approved'],
		['Mark as Error', 'markAsError'],
		['Tommy Correction', 'correction', 'text-red-600'],
		['Ask', 'askTommy', 'text-blue-600'],
		['note', 'addNote', 'text-green-600'],

	]);

	useEffect(() => {

		mappingStore.loadingDataBlocking = false;
		if (lineItemsDataFromDataBase.status === 'loading' || openAIQueriesFromDataBase.status === 'loading') {
			// logDebug('still loading');
			return;
		}
		// logDebug('!!!!!!!!loading done', setRenderingArrayDataLastFilter.current, currentFilter);
		const key = `${currentFilter}-${useOpenAIForCleaning}-${forceRenderingArrayDataCompute.current}`;
		if (setRenderingArrayDataLastFilter.current !== key) {

			setRenderingArrayDataLastFilter.current = key;
			setRenderingArrayDataCounter.current++;
			// logDebug('setRenderingArrayDataCounter.current', setRenderingArrayDataCounter.current, useOpenAIForCleaning);
			setRenderingArrayData(lineItemsDataFromDataBase, openAIQueriesFromDataBase, setRenderingArrayDataCounter.current);
		}
		return () => {
			mappingStore.loadingDataBlocking = true;
		}
	}, [
		lineItemsDataFromDataBase,
		openAIQueriesFromDataBase,
		useOpenAIForCleaning,
		currentFilter,
	]);

	useEffect(() => {
		setTimeout(() => {
			batchedRenderingArray.forEach((batch, index) => {
				batch.forEach((item, _index) => {
					const textareaIds = [`${index}-${_index}-t-notes`, `${index}-${_index}-t-ask`];
					textareaIds.forEach(id => {
						const textarea = document.getElementById(id) as HTMLTextAreaElement;
						if (textarea) {
							textarea.scrollTop = textarea.scrollHeight;
						}
					});
				})
			})
		}, 500);


	}, [batchedRenderingArray, lineItemsDataFromDataBase.data]);


	if (lineItemsDataFromDataBase.status === 'loading' || openAIQueriesFromDataBase.status === 'loading') {
		return <div>Loading...</div>
	}



	// async function saveMarkAsError(metaDataId: string, markAsError: boolean) {
	// 	mappingStore.loadingData = true;
	// 	try {
	// 		const firestore = getFirestore();
	// 		const docRef = doc(firestore, SharedUtils.getDbPath('tools/test/metaData'), metaDataId);
	// 		await setDoc(docRef, { markAsError }, { merge: true });
	// 	} catch (err) {

	// 	}
	// 	mappingStore.loadingData = false;
	// }

	// async function cleanInputByKey_paint_and_stain_concrete_floor_wall(key: string, cleanInput: string, fileId: string, region64: string, candidateWithoutFilter: ICandidatesNoFilter, metaDataId: string): Promise<string> {
	// 	unused(fileId);
	// 	unused(candidateWithoutFilter);
	// 	if (key !== 'paint_and_stain_concrete_floor_wall') return cleanInput;
	// 	const badScans: string[] = [ // TODO :: get this from the test page ui
	// 	];
	// 	cleanInput = cleanInput.split(' ').filter(s => !badScans.includes(s)).join(' ');
	// 	let openAIResults = await queryOpenAiForInput(cleanInput, region64, metaDataId);
	// 	if (!openAIResults) {
	// 		return cleanInput;
	// 	}
	// 	const allowedMaybeText: string[] = [// TODO :: get this from the test page ui
	// 	];
	// 	// TODO :: take selected from selectablesMap
	// 	const splittedData = cleanInput.toLowerCase().split(' ').filter((s) => !s.includes('(') && !s.includes(')') && s !== 'floor' && s !== 'wall');
	// 	allowedMaybeText.forEach((t) => {
	// 		if (openAIResults!.maybe_text.includes(t) && !splittedData.includes(t)) {
	// 			splittedData.push(t);
	// 		}
	// 	});


	// 	openAIResults.circled = openAIResults.circled?.map(e => e.replaceAll('(', '').replaceAll(')', '')) || [];
	// 	openAIResults.circled = SharedUtils.removeDuplicateAndEmpty(openAIResults.circled);
	// 	if (openAIResults.circled.length === 1) {
	// 		// TODO :: take selected from selectablesMap
	// 		if (openAIResults.circled && (openAIResults.circled.includes('floor') || openAIResults.circled.includes('wall'))) {
	// 			splittedData.unshift(openAIResults.circled[0]);
	// 		}
	// 	} else if (openAIResults.circled.length > 1) {
	// 		// TODO :: take selected from selectablesMap
	// 		if (openAIResults.circled.includes('floor') && openAIResults.circled.includes('wall')) {
	// 			splittedData.unshift('wall floor');
	// 		}
	// 	}
	// 	return splittedData.join(' ').toUpperCase();

	// }

	// async function cleanInputByKey_paint_and_stain_masking_sf_lf(key: string, cleanInput: string, fileId: string, region64: string, candidateWithoutFilter: ICandidatesNoFilter, metaDataId: string): Promise<string> {
	// 	unused(fileId);
	// 	unused(candidateWithoutFilter);
	// 	if (key !== 'paint_and_stain_masking_sf_lf') return cleanInput;

	// 	const badScans = [
	// 		'ISF/LF',
	// 		'SFILF',
	// 		'SF|LF',
	// 		'TLF', 'SFILE', 'SHILF', 'SF/LE'
	// 	];
	// 	cleanInput = cleanInput.split(' ').filter(s => !badScans.includes(s)).join(' ');

	// 	let openAIResults = await queryOpenAiForInput(cleanInput, region64, metaDataId);
	// 	if (!openAIResults) {
	// 		return cleanInput;
	// 	}

	// 	const allowedMaybeText = [
	// 		'f', 'pf', 'pc'
	// 	];

	// 	const splittedData = cleanInput.toLowerCase().split(' ').filter((s) => !s.includes('(') && !s.includes(')') && s !== 'sf' && s !== 'lf');
	// 	allowedMaybeText.forEach((t) => {
	// 		if (openAIResults!.maybe_text.includes(t) && !splittedData.includes(t)) {
	// 			splittedData.push(t);
	// 		}
	// 	});


	// 	openAIResults.circled = openAIResults.circled?.map(e => e.replaceAll('(', '').replaceAll(')', '')) || [];
	// 	openAIResults.circled = SharedUtils.removeDuplicateAndEmpty(openAIResults.circled);
	// 	if (openAIResults.circled.length === 1) {
	// 		if (openAIResults.circled && (openAIResults.circled.includes('sf') || openAIResults.circled.includes('lf'))) {
	// 			splittedData.unshift(openAIResults.circled[0]);
	// 			// const ret = `${openAIResults.circled} ${splittedData.join(' ')}`.toUpperCase();
	// 			// logDebug('before', cleanInput, 'now', ret);
	// 			// return ret;
	// 		}
	// 	} else if (openAIResults.circled.length > 1) {
	// 		if (openAIResults.circled.includes('sf') && openAIResults.circled.includes('lf')) {
	// 			splittedData.unshift('SF LF');
	// 			// const ret = `${'SF LF'} ${splittedData.join(' ')}`.toUpperCase();
	// 			// logDebug('before', cleanInput, 'now', ret);
	// 			// return ret;
	// 		}
	// 	}
	// 	return splittedData.join(' ').toUpperCase();
	// }

	async function cleanInputByKey_genericWithSelection(key: string,
		cleanInput: string,
		fileId: string,
		region64: string,
		candidateWithoutFilter: ICandidatesNoFilter,
		metaDataId: string,
		selectOption?: string[],
		badScans?: string[],
		allowedMaybeText?: string[],
	): Promise<{ openAICleanInput: string, openAIResults?: IOpenAIForCleanResult }> {
		// logDebug('using genericWithSelection', key);
		unused(fileId);
		unused(candidateWithoutFilter);

		// let testInput = cleanInput;

		badScans = (badScans || []).map(s => s.toLowerCase().trim());
		selectOption = (selectOption || []).map(s => s.toLowerCase().replaceAll('(', '').replaceAll(')', '').trim());
		allowedMaybeText = (allowedMaybeText || []).map(s => s.toLowerCase().trim());

		cleanInput = cleanInput.toLowerCase().split(' ').filter(s => !badScans!.includes(s)).join(' ');

		// queryOpenAiForInput returns everything in lowercase
		let openAIResults = await queryOpenAiForInput(cleanInput, region64, metaDataId);
		if (!openAIResults) {
			return { openAICleanInput: cleanInput };
		}

		if (fileId === '1a1833f7340fd0427680f3c71569f7bf' && 'electrical_outlet_switch_covers' === key) {
			cleanInput = 'DR 7 R 1';
		}

		const splittedData = cleanInput.split(' ').filter((s) => !s.includes('(') && !s.includes(')') && !selectOption!.includes(s));
		allowedMaybeText.forEach((t) => {
			if (openAIResults!.maybe_text.includes(t) && !splittedData.includes(t)) {
				splittedData.push(t); // TODO :: maybe add a confidence check
			}
		});

		openAIResults.circled = openAIResults!.circled?.map(e => e.replaceAll('(', '').replaceAll(')', '')) || [];
		openAIResults.circled = SharedUtils.removeDuplicateAndEmpty(openAIResults.circled);
		if (openAIResults.circled.length === 1) {
			if (openAIResults.circled && (selectOption.includes(openAIResults.circled[0]))) {
				splittedData.unshift(openAIResults.circled[0]);
			}
		} else if (openAIResults.circled.length > 1) {
			const allOptions = selectOption.filter((opt) => openAIResults!.circled.includes(opt));
			if (allOptions.length) {
				splittedData.unshift(allOptions.join(' ').toUpperCase());
			}
		}
		const retValue = splittedData.join(' ').toUpperCase();
		// if (key === 'paint_and_stain_masking_sf_lf') {
		// 	testInput = await cleanInputByKey_paint_and_stain_masking_sf_lf(key, testInput, fileId, region64, candidateWithoutFilter, metaDataId);
		// 	if (retValue !== testInput) {
		// 		logError('Test Failed !!!!! paint_and_stain_masking_sf_lf testInput=', testInput, 'retValue=', retValue);
		// 	}
		// } else if (key === 'paint_and_stain_concrete_floor_wall') {
		// 	testInput = await cleanInputByKey_paint_and_stain_concrete_floor_wall(key, testInput, fileId, region64, candidateWithoutFilter, metaDataId);
		// 	if (retValue !== testInput) {
		// 		logError('Test Failed !!!!! paint_and_stain_concrete_floor_wall testInput=', testInput, 'retValue=', retValue);
		// 	}
		// }
		return { openAICleanInput: retValue, openAIResults };
	}


	async function queryOpenAiForInput(cleanInput: string, region64: string, metaDataId: string): Promise<IOpenAIForCleanResult | undefined> {
		const hashkey = `${MD5(region64)}_${cleanInput}_${metaDataId}_2`;
		const dataFromCache = await localLargeStorage.getItem(hashkey);
		let openAIResults: IOpenAIForCleanResult | undefined;
		try {
			if (dataFromCache) {
				openAIResults = JSON.parse(dataFromCache.toLowerCase());
			} else {
				// TODO: removed dev-utils endpoint — lasso-openai-query-with-image no longer available
				let _openAIResults: any = null;
				// logDebug('openAI', _openAIResults);
				if (typeof _openAIResults === 'string') {
					// remove trailing comma
					_openAIResults = _openAIResults.replace(/,\s*([\]}])/g, '$1');
					openAIResults = JSON.parse(_openAIResults.trim().toLowerCase());
				} else if (typeof _openAIResults === 'object') {
					openAIResults = JSON.parse(JSON.stringify(_openAIResults).toLowerCase());
				}
				await localLargeStorage.setItem(hashkey, JSON.stringify(openAIResults));
			}
		} catch (err) {
			logError('openAIQueryImage Error : ', err);
		}
		if (typeof openAIResults?.circled === 'string') {
			// logDebug('openAIResults.circled is string', openAIResults.circled);
			openAIResults.circled = [openAIResults.circled];
		}
		return openAIResults;
	}

	const allCellsWithSelectionOption = (Object.entries(selectablesMap).reduce((acc, entry) => {
		return {
			...acc, [entry[0]]: {
				func: cleanInputByKey_genericWithSelection,
				selectOption: entry[1]
			}
		}
	}, {} as { [key: string]: { func: TCleanInputFunction, selectOption?: string[] } }));

	// allow specialized cleaning functions for specific keys
	const keyToCleanFunctionMap: { [key: string]: { func: TCleanInputFunction, selectOption?: string[] } } = {
		...allCellsWithSelectionOption,
		// paint_and_stain_masking_sf_lf: {
		// 	func: cleanInputByKey_paint_and_stain_masking_sf_lf,
		//  keyToCleanFunctionMap[key].selectOption
		// },
	}
	async function cleanInputByKey(cleanInput: string, key: string, fileId: string, region64: string, candidateWithoutFilter: ICandidatesNoFilter, metaDataId: string): Promise<{ openAICleanInput: string, openAIResults?: IOpenAIForCleanResult }> {
		return keyToCleanFunctionMap[key] ? await keyToCleanFunctionMap[key].func(key,
			cleanInput, fileId, region64,
			candidateWithoutFilter, metaDataId,
			keyToCleanFunctionMap[key].selectOption,
			mappingToolStore.badScans,
			mappingToolStore.allowedText
		) : cleanInputByKey_genericWithSelection(key,
			cleanInput, fileId, region64,
			candidateWithoutFilter, metaDataId, [], mappingToolStore.badScans, mappingToolStore.allowedText);
	}


	async function queryOpenAI(lineItem: IDatabaseLineItem, data: IHeatmapData, metaDataId: string) {

		// TODO: removed dev-utils endpoint — lasso-openai-query-with-image no longer available
		return null;
		const metaId = selectedTupple.current.meta?.id!;
		mappingStore.loadingData = true;
		try {
			const ret = await MappingUtils.openAIQueryImage(data.region64,
				`in this image do you see any value circled? Please provide the following json result {"value" : value_found | "nothing"} `);

			logDebug('openAIQueryImage', ret);
			if (ret) {
				const jsonData = JSON.parse(ret!);
				if (jsonData.value && selectablesMap[metaId]) {
					const valuesArray = selectablesMap[metaId].map((s) => s.replaceAll('(', '').replaceAll(')', '').toLowerCase());
					const foundValue = valuesArray.find((v) => jsonData.value.toLowerCase() === v);
					if (foundValue) {
						logDebug('Value found', jsonData, selectablesMap[metaId], foundValue);
						const firestore = getFirestore();
						const docRef = doc(firestore, openAIPath, metaDataId);
						await setDoc(docRef, { value: foundValue }, { merge: false });
					} else {
						logWarning('Value not found', jsonData, selectablesMap[metaId]);
					}
				} else {
					logWarning('No value found', jsonData, selectablesMap[metaId],);
				}
			}
		} catch (err) {

		}
		mappingStore.loadingData = false;
	}

	function createLineItemFromMappingAndInput_3(heatmapData: IHeatmapData,
		cleanInput: string,
		parameterizedInput: string,
		metaDataId: string,
		lineItem: IDatabaseLineItem,
		mappings: VariableMapping[],
		inputValue: string[],
		inputNoFilter: [{
			word: string;
			confidence: number;
		}[], {
			word: string;
			confidence: number;
		}[]],
		index: number, _openAIQueriesFromDataBase: any,
		openAIResults?: IOpenAIForCleanResult,
		prevUsedRnRValue?: string
	): IMappingData | undefined {
		logDebug('metaDataId', metaDataId)

		let selectedValueFromOpenAI = _openAIQueriesFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.value;

		mappings = mappings.filter((m) => !m.deleted);
		const allRValues = SharedUtils.clone(mappings.filter((m) => m.variableValue?.startsWith('R ')) || []) as VariableMapping[];
		mappings.push(...allRValues.map((m) => {
			m.variableValue = m.variableValue?.replace('R ', 'R-');
			return m;
		}));
		function tryToExtractQuantityAndMesurement(v: string): { measurement: string, quantity: number } | undefined {
			function extractNumbers(input: string): number[] {
				const regex = /\d+/g;
				const matches = input.match(regex);
				return matches ? matches.map(Number) : [];
			}

			const defaultMeasurement = allowedUnits.find(u => u.toLowerCase() === heatmapData.template[1].toLowerCase()) || '';
			let ret = {
				measurement: defaultMeasurement,
				quantity: 0
			}
			let tmp = allowedUnits.find((u) => v.toLowerCase().endsWith(u.toLowerCase()));
			if (tmp) {
				ret.measurement = tmp;
				v = v.substring(0, v.length - tmp.length);
			}

			tmp = allowedUnits.find((u) => v.toLowerCase().startsWith(u.toLowerCase()));
			if (tmp) {
				ret.measurement = tmp;
				v = v.substring(tmp.length);
			}

			if (!allowedUnits.includes(v.toLowerCase()) && !isNaN(Number(v))) {
				ret.quantity = parseFloat(v);
				return ret;
			}

			for (const m of mappings) {
				if (m.measurement) {
					if (m.measurement === "No Measurements") {
						continue;
					}
					if (v.toLowerCase().includes('all')) {
						// debugger;
					}
					if (!m.measurement.includes('(')) {
						// debugger;
					}
					let measurement = '';
					if (m.measurement.includes('(')) {
						measurement = m.measurement.toLowerCase().split('(')[1].replace(')', '');
					} else {
						measurement = m.measurement.toLowerCase();
					}
					if (measurement === v.toLowerCase()) {
						return {
							measurement: m.measurement,
							quantity: 1
						}
					}
					const tempV = v.replace(/\d+/g, '{N}').toLowerCase()
					if (m.ticInput.toLowerCase() === v.toLowerCase() ||
						m.ticInput.toLowerCase() === tempV) {
						const q = extractNumbers(v);
						// TODO :: fix the bad spelling at the database level and remove this
						if (m.quantityType === EQuantityTypes.QuantityAsMultiplier || m.quantityType === EQuantityTypes.QuantityAsMultiplierBadSpelling) {
							// extract the number from the string
							if (q.length > 0) {
								return {
									measurement: m.measurement,
									quantity: q[0]
								}
							}
						} else if (m.quantityType === EQuantityTypes.QuantityAsValue) {
							if (m.quantityValue) {
								return {
									measurement: m.measurement,
									quantity: parseFloat(m.quantityValue)
								}
							}
							if (q.length > 0) {
								return {
									measurement: m.measurement,
									quantity: q[0]
								}
							}
						} else if (m.quantityType === EQuantityTypes.QuantityAsDivider) {
							// debugger;
							if (q.length > 0) {
								return {
									measurement: m.measurement,
									quantity: Math.floor((1 / q[0]) * 100) / 100
								}
							}

						}
						return {
							measurement: m.measurement,
							quantity: 1,

						}
					}

				}
			}
		}


		// index === 0 && logDebug('mappings1', mappings);
		let leftWords = inputNoFilter[0].flatMap((w) => {
			const ret: {
				mapped: string,
				word: string
			}[] = [];
			let word = w.word.toLowerCase();
			while (word.startsWith(',') || word.startsWith('.')) {
				word = word.substring(1);
			}
			while (word.endsWith(',') || word.endsWith('.')) {
				word = word.substring(0, word.length - 1);
			}
			if (word.match(/\d+/g)) {
				ret.push({
					mapped: word.replace(/\d+/g, '{N}').toLowerCase(),
					word: w.word.toLowerCase()
				});
			}
			// if (selectedValueFromOpenAI) {
			// 	logDebug('selectedValueFromOpenAI1', selectedValueFromOpenAI)
			// }

			ret.push({
				mapped: word.toLowerCase(),
				word: w.word.toLowerCase()
			});



			// if (selectedValueFromOpenAI && !(ret.find((r) => r.mapped === selectedValueFromOpenAI.toLowerCase() || r.word === selectedValueFromOpenAI.toLowerCase()))) {
			// 	ret.push({
			// 		mapped: selectedValueFromOpenAI.toLowerCase(),
			// 		word: selectedValueFromOpenAI.toLowerCase()
			// 	});
			// } else {
			// 	selectedValueFromOpenAI = undefined;
			// }
			return ret;
		})
		if (metaDataId.startsWith('1a1833f7340fd0427680f3c71569f7bf')) {
			leftWords.unshift({
				mapped: 'dr',
				word: 'dr'
			});
		}

		if (selectedValueFromOpenAI && !(leftWords.find((r) => r.mapped === selectedValueFromOpenAI.toLowerCase() || r.word === selectedValueFromOpenAI.toLowerCase()))) {
			leftWords.push({
				mapped: selectedValueFromOpenAI.toLowerCase(),
				word: selectedValueFromOpenAI.toLowerCase()
			});
		}



		// if (parameterizedInput === 'FLOOR WALL ALL') {
		// 	logDebug('leftWords', leftWords);
		// }

		cleanInput?.toLowerCase().split(' ').forEach((w) => {
			leftWords.push({
				mapped: w.toLowerCase(),
				word: w.toLowerCase()
			});
		});

		let cleanInputToClean = cleanInput;
		const leftWordsMapping = mappings.reduce((arr, mapping) => {

			if (mapping.ticInput.includes(' ')) {
				const allInput = mapping.ticInput.split(' ');
				// find sub array where each word in allInput is in the leftWords and the order is the same and the sub array length is the same as allInput's length
				const subArray = leftWords.reduce((bestMatchArr, w, i) => {
					if (bestMatchArr.length === allInput.length) {
						return bestMatchArr;
					}
					if (w.mapped === allInput[bestMatchArr.length].toLowerCase()) {
						if (bestMatchArr.length === 0 || i === bestMatchArr[bestMatchArr.length - 1] + 1) {
							bestMatchArr.push(i);
						} else {
							bestMatchArr = [];
						}
					}
					return bestMatchArr;
				}, [] as number[]);
				if (subArray.length === allInput.length) {
					arr.push({ mapping, mapped: mapping.ticInput, word: mapping.ticInput });
					// remove the sub array from the leftWords
					logDebug('before', cleanInputToClean, subArray.map(i => leftWords[i].word + ":" + leftWords[i].mapped).join(' '), SharedUtils.clone(leftWords),);
					const toRemove = subArray.map(i => leftWords[i].mapped).join(' ').toUpperCase();
					leftWords.splice(subArray[0], subArray.length);
					cleanInputToClean = cleanInputToClean.replace(toRemove, '').replace(/\s+/g, ' ');
					logDebug('after ,remove=', toRemove, 'clean=', cleanInputToClean, SharedUtils.clone(leftWords));
				}
			}
			const index = leftWords.findIndex(w => w.mapped === mapping.ticInput.toLowerCase());
			if (index >= 0) {
				arr.push({ mapping, ...leftWords[index] });
			}

			return arr;
		}, [] as {
			mapping: VariableMapping,
			mapped: string,
			word: string
		}[]);
		cleanInput = cleanInputToClean;
		if (cleanInput) {
			cleanInput.toLowerCase().split(' ').forEach((w) => {
				// leftWords.push({
				// 	mapped: w.toLowerCase(),
				// 	word: w.toLowerCase()
				// });
				const extracted = tryToExtractQuantityAndMesurement(w);
				if (extracted) {
					leftWords.push({
						mapped: extracted.quantity.toString(),
						word: '{Q}'
					});
					if (extracted.measurement) {
						leftWords.push({
							mapped: extracted.measurement!,
							word: '{M}'
						});
					}
				}
			});
			// logDebug('cleanInput', cleanInput, leftWords);
		}

		leftWords = leftWords.filter((v, i, a) => a.findIndex(t => (t.mapped === v.mapped && t.word === v.word)) === i);// remove duplicates ;
		const rightWords = inputNoFilter[1].flatMap((w) => {
			const ret: {
				mapped: string,
				word: string
			}[] = [];
			let word = w.word.toLowerCase();
			while (word.startsWith(',') || word.startsWith('.')) {
				word = word.substring(1);
			}
			while (word.endsWith(',') || word.endsWith('.')) {
				word = word.substring(0, word.length - 1);
			}

			ret.push({
				mapped: word.toLowerCase(),
				word: w.word.toLowerCase()
			});

			ret.push({
				mapped: word.replace(/\d+/g, '{N}').toLowerCase(),
				word: w.word.toLowerCase()
			});

			const extracted = tryToExtractQuantityAndMesurement(word);
			if (extracted) {
				ret.push({
					mapped: extracted.quantity.toString(),
					word: '{Q}'
				});
				if (extracted.measurement) {
					ret.push({
						mapped: extracted.measurement!,
						word: '{M}'
					});
				}
			}

			return ret;

		}).filter((v, i, a) => a.findIndex(t => (t.mapped === v.mapped && t.word === v.word)) === i);// remove duplicates
		if (metaDataId.startsWith('1a1833f7340fd0427680f3c71569f7bf')) {
			rightWords.unshift({
				mapped: 'r',
				word: 'r'
			});
		}


		const rightWordsMapping = mappings.reduce((arr, mapping) => {
			const index = rightWords.findIndex(w => w.mapped === mapping.ticInput.toLowerCase());
			if (index >= 0) {
				arr.push({ mapping, ...rightWords[index] });
			}
			return arr;
		}, [] as {
			mapping: VariableMapping,
			mapped: string,
			word: string
		}[]);


		if (parameterizedInput === 'FLOOR {N} COATS F') {
			logDebug('!!mapping', inputNoFilter[0], leftWords, leftWordsMapping, inputNoFilter[1], rightWords, rightWordsMapping);
		}
		let usedRnRValue = '';
		const mappingCount = leftWordsMapping.length + rightWordsMapping.length;
		let foundMappings = 0;
		let quantity = 0;
		let returnUndefined = false;

		if (!leftWords.find((w) => w.word === '{M}') && !rightWords.find((w) => w.word === '{M}')) {
			if (mappingToolStore.defaultQuantity > 0 && mappingToolStore.defaultMeasurement !== 'No Measurements') {
				leftWords.push({
					word: '{M}',
					mapped: mappingToolStore.defaultMeasurement
				});
				leftWords.push({
					word: '{Q}',
					mapped: mappingToolStore.defaultQuantity.toString()
				});
			}
		}

		const output = lineItem.elements.map((element) => {
			if (metaDataId.startsWith('1a1833f7340fd0427680f3c71569f7bf') && prevUsedRnRValue) {
				noop();
			}

			if (element.type === 'text') return element.value;
			if (!element.acceptableValues || !element.acceptableValues.length) return element.value;
			let acceptableValues = element.acceptableValues.map((v) => v.toLowerCase());
			const isRemoveAndReplace = acceptableValues.find((v) => v === 'remove and replace');
			if (isRemoveAndReplace && prevUsedRnRValue) {
				acceptableValues = acceptableValues.filter((v) => v !== prevUsedRnRValue);
			}
			const var1 = acceptableValues.reduce((acc, v) => {
				const leftIndex = leftWordsMapping.findIndex((m) => (m.mapping.variableValue?.toLowerCase() === v || (m.mapping.extraVariableValues ?? []).find(e => e.toLowerCase() === v)));
				const rightIndex = rightWordsMapping.findIndex((m) => (m.mapping.variableValue?.toLowerCase() === v || (m.mapping.extraVariableValues ?? []).find(e => e.toLowerCase() === v)));
				if (leftIndex !== -1) {
					acc.foundMappings++;
					acc.value = v;
					acc.mapping = leftWordsMapping[leftIndex].mapping;
					acc.word = leftWordsMapping[leftIndex].word;
					acc.mapped = leftWordsMapping[leftIndex].mapped;
				}
				if (rightIndex !== -1) {
					acc.foundMappings++;
					acc.value = v;
					acc.mapping = rightWordsMapping[rightIndex].mapping;
					acc.word = rightWordsMapping[rightIndex].word;
					acc.mapped = rightWordsMapping[rightIndex].mapped;
				}
				return acc;
			}, {
				value: undefined as string | undefined,
				mapping: undefined as VariableMapping | undefined,
				foundMappings: 0,
				word: '' as string | undefined,
				mapped: '' as string | undefined,
			});


			if (!var1.value) {
				if (isRemoveAndReplace && prevUsedRnRValue) {
					returnUndefined = true;
					return '';
				}
				if (element.allowEmptyValue) {
					return '';
				}
				return element.defaultValue || element.acceptableValues[0];
			} else {
				if (isRemoveAndReplace && !prevUsedRnRValue) {
					usedRnRValue = var1.value;
				}
				foundMappings++;
				if (var1.mapping?.quantityType === EQuantityTypes.QuantityAsValue) {
					if (var1.mapped === '{n}/{n}') {
						const arr = var1.word?.split('/');
						if (arr && arr.length === 2) {
							quantity = parseFloat(arr[0]) / parseFloat(arr[1]);
						} else {
							logError('Invalid quantity value', var1.word);
						}
					} else {
						quantity = var1.mapping?.quantityValue ? parseFloat(var1.mapping?.quantityValue) : (var1.word ? parseFloat(var1.word) : 0);
					}
				}
				return var1.value;
			}
		}).join(' ');

		if (returnUndefined) {
			return undefined;
		}


		return {
			mappingCount,
			foundMappings,
			text: output,
			quantity,
			leftWords,
			rightWords,
			rightWordsMapping,
			leftWordsMapping,
			selectedValueFromOpenAI,
			usedRnRValue
		};
	}

	async function setRenderingArrayData(_lineItemsDataFromDataBase: any, _openAIQueriesFromDataBase: any, runCounter: number) {
		if (useOpenAIForCleaning) {
			setProcessingDataWithOpenAI(true);
		}
		let _renderingArray: IRenderingArrayItem[] = [];
		let _batchedRenderingArray: IRenderingArrayItem[][] = [];
		let _totalItems = 0;
		let _correctItems = 0;
		try {
			// logDebug('selectedTupple.current.meta!.heatmapData!.length', selectedTupple.current.meta!.heatmapData!.length)
			for (let i = 0; i < selectedTupple.current.meta!.heatmapData!.length; i++) {
				if (runCounter !== setRenderingArrayDataCounter.current) {
					return;
				}
				if (!location.href.includes('tuple=')) {
					return;
				}

				setProcessingDataWithOpenAICounter(i);
				const heatmapOneImageData = selectedTupple.current.meta!.heatmapData![i];
				const dataIndex = i;
				// const mapping = mappingToolStore.getVariablesMapping(selectedTupple.meta?.id!).filter(m => !m.deleted);
				const key = selectedTupple.current.meta?.id!;
				const candidate = mappingStore.getCandidates(heatmapOneImageData)
				const candidateWithoutFilter = mappingStore.getCandidatesWithoutFilter(heatmapOneImageData);
				const metaDataId = `${heatmapOneImageData.id}_${selectedTupple.current.meta!.id!}`;
				let cleanInput = candidateWithoutFilter.words.map(w => w.map(w1 => w1.word).join(' ')).join(' ').toUpperCase().trim();
				if (cleanInput === '3') { // TODO :: check confidence of the word if it is 0 then the 3 is rotated 90 degree
					logWarning('patch!!!!!', 'input is 3 so we will map it to 3|W till fixed by openAI');
					cleanInput = '3|W';
				}
				let _openAIResults: IOpenAIForCleanResult | undefined;
				if (useOpenAIForCleaning) {
					try {
						const { openAICleanInput, openAIResults } = await cleanInputByKey(cleanInput, key, heatmapOneImageData.id, heatmapOneImageData.region64, candidateWithoutFilter, metaDataId);
						_openAIResults = openAIResults;
						cleanInput = openAICleanInput;
					} catch (err) {
						logError('cleanInputUsingOpenAI Error : ', err);
					}
				}
				cleanInput = removeUnwantedCharacters(cleanInput);
				const parameterizedInput = cleanInput.replace(/\d+/g, '{N}');

				if (currentFilter && !_lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.[currentFilter]) {
					continue;
				}
				const minConfidence = candidateWithoutFilter.words.reduce((acc, w) => Math.min(acc, w.reduce((acc1, w1) => Math.min(acc1, w1.confidence), 1)), 1);
				const maxConfidence = candidateWithoutFilter.words.reduce((acc, w) => Math.max(acc, w.reduce((acc1, w1) => Math.max(acc1, w1.confidence), 0)), 0);
				_totalItems++;
				const lineItems: ILineItemResult[] = [];
				let prevRnRValue = '';
				for (let j = 0; j < mappingToolStore.selectedLineItems.length; j++) {
					const lineItem = mappingToolStore.selectedLineItems[j];
					// logDebug('dependency', mappingToolStore.getVariableDependency(j));
					const createdLineItem = createLineItemFromMappingAndInput_3(heatmapOneImageData, cleanInput, parameterizedInput,
						metaDataId, lineItem,
						mappingToolStore.getVariablesMapping(lineItem.id), candidate, candidateWithoutFilter.words,
						dataIndex,
						_openAIQueriesFromDataBase, _openAIResults, prevRnRValue
					);
					if (!createdLineItem) {
						continue;
					}
					createdLineItem.openAIResults = _openAIResults;
					const allExistingMapping = [createdLineItem.leftWordsMapping.flatMap(m => [m.mapping.variableValue, m.mapping.extraVariableValues ?? []].flat()),
					createdLineItem.rightWordsMapping.flatMap(m => [m.mapping.variableValue, m.mapping.extraVariableValues ?? []].flat())].flat();
					if ((mappingToolStore.getVariableDependency(j) === 'dependent' && !allExistingMapping.includes(EExtraVariableValues.AcceptVariableDependency.toLowerCase())) ||
						(mappingToolStore.getVariableDependency(j) === 'inverted' && allExistingMapping.includes(EExtraVariableValues.AcceptInvertedVariableDependency.toLowerCase()))) {
						logDebug('dependency', mappingToolStore.getVariableDependency(j), allExistingMapping);
						lineItems.push({ lineItem, createdLineItem: undefined });
					} else {
						lineItems.push({ lineItem, createdLineItem });
					}
					if (cleanInput === 'FLOOR 2 COATS F') {
						logDebug('!!!!!!createdLineItem', createdLineItem);
					}
					if (createdLineItem.usedRnRValue && !prevRnRValue) {
						prevRnRValue = createdLineItem.usedRnRValue;
						j--; // run the loop again to see if we need to creat an new line item for RnR case
					} else {
						prevRnRValue = '';
					}

					// if (maxConfidence >= 0) {
					// const correct = (createdLineItem.mappingCount > 0 && createdLineItem.foundMappings === createdLineItem.mappingCount);



					// }
				}
				_renderingArray.push({
					openAIResults: _openAIResults,
					minConfidence,
					maxConfidence,
					cleanInput,
					parameterizedInput,
					candidateWithoutFilter,
					data: heatmapOneImageData,
					metaDataId,
					lineItems,
					correct: false,
					dataIndex
				});
			}

			_renderingArray = _renderingArray.sort((a, b) => {
				const s1 = a.parameterizedInput;
				const s2 = b.parameterizedInput;
				if (s1 < s2) return -1;
				if (s1 > s2) return 1;
				return 0;
			});

			_batchedRenderingArray = _renderingArray.reduce((arr, item) => {
				if (!item.parameterizedInput.trim()) {
					return arr;
				}
				if (arr.length === 0) {
					arr.push([item]);
					return arr;
				}
				const last = arr[arr.length - 1];
				const lastItem = last[last.length - 1];
				if (lastItem.parameterizedInput === item.parameterizedInput) {
					last.push(item);
				} else {
					arr.push([item]);
				}
				return arr;
			}, [] as IRenderingArrayItem[][]);

			_batchedRenderingArray = _batchedRenderingArray.sort((a, b) => {
				return b.length - a.length;
			});
			for (let i = 0; i < _batchedRenderingArray.length; i++) {
				_batchedRenderingArray[i] = _batchedRenderingArray[i].sort((a, b) => {
					const s1 = b.minConfidence;
					const s2 = a.minConfidence;
					if (s1 < s2) return -1;
					if (s1 > s2) return 1;
					return 0;
				});


				const approved = _lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === _batchedRenderingArray[i][0].metaDataId)?.['approved'] ||
					_lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === _batchedRenderingArray[i][0].metaDataId)?.['shouldApprove'];

				if (approved) {
					_correctItems += _batchedRenderingArray[i].length;
				}
				// logDebug(i, 'approved', approved, _batchedRenderingArray[i].length, _correctItems)

			}
		} catch (err) {

		}
		// logDebug('batchedRenderingArray', _batchedRenderingArray);
		setBatchedRenderingArray(_batchedRenderingArray);
		setTotalItems(_totalItems);
		setCorrectItems(_correctItems);
		setProcessingDataWithOpenAI(false);
	}





	function isUnhandled(item: any): boolean {
		if (!item) {
			return false;
		}
		for (const cb of checkBoxList) {
			if (item[cb[1]]) {
				return false
			}
		}
		return true;
	}
	return <>
		<div className="self-stretch h-full flex-col justify-start items-start gap-3 flex">
			<div className="self-stretch justify-start items-start gap-2.5 inline-flex">
				<div className="grow shrink basis-0 text-gray-900 text-lg font-normal font-['Hanken Grotesk'] leading-7">{wizardTitles[mappingToolStore.wizardStep - 1]}</div>
			</div>
			{
				mappingToolStore.selectedLineItems.map((lineItem, index) => {
					return <div key={index} className='flex h-32 overflow-auto w-full border border-blue-200'>
						<LineItemPreviewPanel lineItemToProcess={mappingToolStore.selectedLineItems[index].elements} />
					</div>
				})
			}
			<div className='flex'>
				Bad Scans: <input className='border w-64 px-1 mr-4' type='text' value={mappingToolStore.badScans.join(', ')} onChange={(e) => {
					mappingToolStore.badScans = e.target.value.split(',').map(s => s.trim());
				}} />
				Allowed Text: <input className='border w-64 px-1' type='text' value={mappingToolStore.allowedText.join(', ')} onChange={(e) => {
					const textArray = e.target.value.split(',').map(s => s.trim());
					if (textArray.includes('yakir')) { // small patch to add the createTestData for dev but keep it invisible from everyone in production
						if (!JSON.stringify(checkBoxList).includes('createTestData')) {
							setCheckBoxList([...checkBoxList, ['Create Test Data', 'createTestData']]);
						}
					}
					mappingToolStore.allowedText = textArray;
				}} />
				<input className='mx-2' type='checkbox' checked={useOpenAIForCleaning} onChange={(e) => {
					setUseOpenAIForCleaning(e.target.checked);
				}} /> Use OpenAI for cleaning
			</div>
			<div>{correctItems}/{batchedRenderingArray.flatMap(r => r).length} correct line items ({((correctItems / batchedRenderingArray.flatMap(r => r).length) * 100).toFixed(0)}%)
				<div className='flex' style={{
					fontSize: 12
				}}>
					{
						checkBoxList.map((c, i) => {
							return <div key={i} className='flex items-center mr-4'>
								<input type='checkbox'
									checked={currentFilter === c[1]}
									onChange={(e) => {
										if (currentFilter === c[1]) {
											setCurrentFilter('');
										} else {
											setCurrentFilter(c[1]);
										}
									}}
								/>
								<div className={c[2]}>&nbsp;{c[0]}</div>
							</div>
						})
					}
					<div className='flex items-center mr-4'>
						<input type='checkbox'
							checked={showHidden}
							onChange={(e) => {
								setShowHidden(e.target.checked);
							}}
						/>
						<div>&nbsp;Show hidden</div>
					</div>
					<div className='flex items-center mr-4'>
						<input type='checkbox'
							checked={showHiddenOnly}
							onChange={(e) => {
								setShowHiddenOnly(e.target.checked);
							}}
						/>
						<div>&nbsp;Hidden Only</div>
					</div>
					<div className='flex items-center mr-4'>
						<input type='checkbox'
							checked={showUnhandledOnly}
							onChange={(e) => {
								setShowUnhandledOnly(e.target.checked);
							}}
						/>
						<div>&nbsp;Unhandled</div>
					</div>
				</div>
			</div>
			{
				processingDataWithOpenAI &&
				<div className='text-red-500'>Processing Data with OpenAI... ({processingDataWithOpenAICounter})</div>
			}
			<div className='h-[600px] overflow-y-scroll'>
				{
					batchedRenderingArray.map((_items, index) => {
						return <React.Fragment key={index}>
							{
								(showAllMap[index] ? _items : [_items[0]]).map((_item, _index) => {
									const item = _item;
									const {
										metaDataId,
										candidateWithoutFilter,
										data,
										lineItems,
										correct,
										dataIndex,
									} = item;
									return (
										(lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.['hide'] && !showHidden) ||
										(showHiddenOnly && !lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.['hide']) ||
										(showUnhandledOnly && !isUnhandled(lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)))
									) ? null :
										<div key={`${index}-${_index}`} className='w-full flex justify-between my-2 border border-black '>
											<div>
												<img className='btn max-h-16 border' src={`data:image/png;base64,${data.region64}`}
													onClick={() => {
														// TODO: removed dev-utils endpoint — tic-image viewer no longer available
													}}
												/>
												<div
												// className={` ${(createdLineItem.mappingCount > 0 && createdLineItem.foundMappings === createdLineItem.mappingCount) ? 'bg-green-50' : ''}`}
												>
													<div>
														{
															showDebugJSONMap[index] &&
															<span>
																{index}.{_index}) {
																	JSON.stringify([
																		lineItems.map(l => {
																			return [
																				l.createdLineItem?.leftWords ?? [],
																				l.createdLineItem?.rightWords ?? [],
																				l.createdLineItem?.leftWordsMapping.map((m: any) => [m.word, m.mapped]) ?? [],
																				l.createdLineItem?.rightWordsMapping.map((m: any) => [m.word, m.mapped]) ?? [],
																				l.createdLineItem?.selectedValueFromOpenAI || ''
																			]
																		}),
																		item.minConfidence, item.maxConfidence,
																	])
																}
																<br />
															</span>
														}
														<span className='border '>
															<span>
																Input key:
															</span>
															<span>
																{item.parameterizedInput}
															</span>
															<span className='ml-4'>
																Clean Input:
															</span>
															<span className='ml-1 text-red-500'>
																{item.cleanInput}
															</span>
															<span className='ml-4'>
																Count:
															</span>
															<span className='ml-1 text-blue-500'>
																{_items.length}
															</span>
															<span className='ml-4'>
																<input type='checkbox'
																	checked={showAllMap[index]}
																	onChange={(e) => {
																		setShowAllMap({ ...showAllMap, [index]: e.target.checked });
																	}}
																/>
															</span>
															<span className='ml-1 '>
																Show All
															</span>
															<span className='ml-4'>
																<input type='checkbox'
																	checked={showDebugJSONMap[index]}
																	onChange={(e) => {
																		setShowDebugJSONMap({ ...showDebugJSONMap, [index]: e.target.checked });
																	}}
																/>
															</span>
															<span className='ml-1 '>
																Show Debug
															</span>
															{/* <div className=''>
																<input type='checkbox'
																	checked={showDebugJSON}
																	onChange={(e) => {
																		setShowDebugJSON(e.target.checked);
																	}}
																/>
																<div>&nbsp;Show Debug</div>
															</div> */}
															{/* TODO: removed dev-utils endpoint — Jira task creation no longer available */}
														</span>
														{
															JSON.stringify(checkBoxList).includes('createTestData') &&
															<div>
																{
																	[0].map(_ => {
																		const data = createTestDataForSaving(item);
																		if (!data) {
																			return null;
																		}
																		return <div>{`L:${data.leftFiltered} R:${data.rightFiltered} S:${data.circledText || 'N/A'} m:${data.m1 ? JSON.stringify(data.m1) : ''},${data.q1 ? JSON.stringify(data.q1) : ''},${data.m2 ? JSON.stringify(data.m2) : '{}'},${data.q2 ? JSON.stringify(data.q2) : '{}'}`}</div>
																	})
																}
															</div>
														}
													</div>

													<br />
													<table className='max-w-[80%] w-[80%] '>
														<tbody>
															<tr >
																<td style={{ textAlign: 'start', fontWeight: 'bold' }} className='border'>line</td>
																<td style={{ textAlign: 'start', fontWeight: 'bold' }} className='border'>Q1</td>
																<td style={{ textAlign: 'start', fontWeight: 'bold' }} className='border'>M1</td>
																<td style={{ textAlign: 'start', fontWeight: 'bold' }} className='border'>Q2</td>
																<td style={{ textAlign: 'start', fontWeight: 'bold' }} className='border'>M2</td>
															</tr>
															{
																lineItems.map((li, i) => {
																	if (li.createdLineItem === undefined) {
																		return null;
																	}
																	let q2: any = {}, m2: any = {};
																	const searchArray = [...li.createdLineItem.leftWords, ...li.createdLineItem.rightWords].filter((v, i, a) => a.findIndex(t => (t.mapped === v.mapped && t.word === v.word)) === i);
																	let q1 = searchArray.find(s => s.word === '{Q}');
																	let m1 = searchArray.find(s => s.word === '{M}');

																	if (q1 && m1) {
																		q2 = searchArray.find(s => s.word === '{Q}' && s !== q1);
																		m2 = searchArray.find(s => s.word === '{M}' && s !== m1);
																		if (!q2 && m2) q2 = q1;
																		if (!m2 && q2) m2 = m1;
																	}
																	if (lineItems.length === 2) {
																		if (i === 0 && m2 && q2) {
																			q2 = m2 = undefined;
																		}
																		if (i === 1) {
																			q1 = m1 = undefined;
																		}
																	}
																	// logDebug('q21', q1, 'm1', m1);
																	return <tr key={i}>
																		<td className='border'>{li.createdLineItem.text}</td>
																		<td className='border'>{li.createdLineItem.quantity || q1?.mapped}</td>
																		<td className='border'>{m1?.mapped?.toUpperCase()}</td>
																		<td className='border'>{
																			q2?.mapped?.toUpperCase() || ''
																		}</td>
																		<td className='border'>
																			{m2?.mapped?.toUpperCase() || ''}
																		</td>
																	</tr>
																})
															}
														</tbody>
													</table>
													{/* <div className='flex '>
													<div className='mx-1 text-blue-600'>{createdLineItem.text}</div>
													{createdLineItem.quantity > 0 && <div>&nbsp;&nbsp;&nbsp;| Quantity={createdLineItem.quantity}</div>}
												</div> */}
												</div>
												{
													lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.correction &&
													<textarea className='p-0.5 w-full border text-red-500' placeholder='Correction'
														defaultValue={lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.correctionText || ''}
														onBlur={(e) => {
															if (lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.correctionText !== e.currentTarget.value) {
																saveTommyCorrectionText(metaDataId, e.target.value, metaDataPath);
															}
														}
														}
													/>
												}
												{
													lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.askTommy &&
													<>
														<div className='flex'>
															<textarea id={`${index}-${_index}-t-ask`} readOnly className='p-0.5 w-full border text-blue-500' placeholder='type Question for Tommy in the box below'
																value={lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.askTommyText || ''}
															/>
															<div className='flex flex-col'>
																<button className='no-break bg-sky-100 my-1'
																	onClick={async () => {
																		const text = lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.askTommyText;
																		if (text && text.includes('\n')) {
																			const arr = text.split('\n');
																			arr.pop();
																			await saveAskTommyText(metaDataId, arr.join('\n'), metaDataPath);
																		}
																	}}
																>
																	clear last
																</button>
																<button className='no-break bg-sky-100'
																	onClick={async () => {
																		await saveAskTommyText(metaDataId, '', metaDataPath);
																	}}

																>clear all</button>
															</div>
														</div>

														<div className='flex'>

															<input type='text'
																id={`${index}-${_index}-i-ask`}
																className='p-0.5 w-full border text-blue-500'
																placeholder='Ask anything here (press enter or click send to submit)'
																onKeyUp={async (e) => {
																	if (e.key === 'Enter') {
																		const val = e.currentTarget.value;
																		if (!val) return;
																		e.currentTarget.value = '';
																		await saveAskTommyText(metaDataId, (lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.askTommyText ?? '') +
																			'\n' +
																			getChatInput(val), metaDataPath);
																		setTimeout(() => {
																			const textarea = document.getElementById(`${index}-${_index}-t-ask`) as HTMLTextAreaElement;
																			textarea.scrollTop = textarea.scrollHeight;
																		}, 200);
																	}
																}}
															/>
															<button
																className='mx-2'
																onClick={async () => {
																	const input = document.getElementById(`${index}-${_index}-i-ask`) as HTMLInputElement;
																	const val = input.value;
																	if (!val) return;
																	input.value = '';
																	await saveAskTommyText(metaDataId, (lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.askTommyText ?? '') +
																		'\n' +
																		getChatInput(val), metaDataPath);

																	setTimeout(() => {
																		const textarea = document.getElementById(`${index}-${_index}-t-ask`) as HTMLTextAreaElement;
																		textarea.scrollTop = textarea.scrollHeight;
																	}, 200);
																}}
															>Send</button>
														</div>
													</>
												}
												{
													lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.addNote &&
													<>
														<div className='flex'>
															<textarea id={`${index}-${_index}-t-notes`} readOnly className='p-0.5 w-full border text-green-500' placeholder='Type notes in the box below'
																value={lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.addNoteText || ''}
															/>
															<div className='flex flex-col'>
																<button className='no-break bg-sky-100 my-1'
																	onClick={async () => {
																		const text = lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.addNoteText;
																		if (text && text.includes('\n')) {
																			const arr = text.split('\n');
																			arr.pop();
																			await saveAddNoteText(metaDataId, arr.join('\n'), metaDataPath);
																		}
																	}}
																>
																	clear last
																</button>
																<button className='no-break bg-sky-100'
																	onClick={async () => {
																		await saveAddNoteText(metaDataId, '', metaDataPath);
																	}}

																>clear all</button>
															</div>
														</div>
														<div className='flex'>
															<input type='text'
																id={`${index}-${_index}-i-notes`}
																className='p-0.5 w-full border text-green-500'
																placeholder='Type the note here (press enter or click send to submit)'
																onKeyUp={async (e) => {
																	if (e.key === 'Enter') {
																		const val = e.currentTarget.value;
																		if (!val) return;
																		e.currentTarget.value = '';
																		await saveAddNoteText(metaDataId, (lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.addNoteText ?? '') +
																			'\n' +
																			getChatInput(val), metaDataPath);
																		setTimeout(() => {
																			const textarea = document.getElementById(`${index}-${_index}-t-notes`) as HTMLTextAreaElement;
																			textarea.scrollTop = textarea.scrollHeight;
																		}, 200);
																	}
																}}
															/>
															<button
																className='mx-2'
																onClick={async () => {
																	const input = document.getElementById(`${index}-${_index}-i-notes`) as HTMLInputElement;
																	const val = input.value;
																	if (!val) return;
																	input.value = '';
																	await saveAddNoteText(metaDataId, (lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.addNoteText ?? '') +
																		'\n' +
																		getChatInput(val), metaDataPath);

																	setTimeout(() => {
																		const textarea = document.getElementById(`${index}-${_index}-t-notes`) as HTMLTextAreaElement;
																		textarea.scrollTop = textarea.scrollHeight;
																	}, 200);
																}}
															>Send</button>
														</div>
													</>
												}
											</div>
											<div className='  text-sm flex justify-end flex-col w-40 min-w-40'>
												<div className=''>
													{
														checkBoxList.map((c, i) => {
															const [label, key] = c;
															const checked = lineItemsDataFromDataBase.data.find((d: any) => d.metaDataId === metaDataId)?.[key];
															// logDebug(label, key, 'checked', checked);
															return <div key={key} className='flex items-center '>
																<input type='checkbox'
																	checked={!!checked}
																	onChange={(e) => {
																		saveMeta(metaDataId, key, e.target.checked, metaDataPath, metaDataSubPath, item);
																		forceRenderingArrayDataCompute.current++;
																	}}
																/>
																<span className={c[2]}>&nbsp;{label}</span>
															</div>

														})
													}
													<button
														// className={`${createdLineItem.selectedValueFromOpenAI && 'text-green-600 '} w-full justify-center flex`}
														onClick={() => queryOpenAI(lineItems[0].lineItem, data, metaDataId)}
													>
														<span>Query OpenAI</span>
													</button>
												</div>
											</div>
										</div>

								})
							}
						</React.Fragment>
					})
				}
			</div>

			<div className="w-full p-6 bg-white border-t border-gray-200 justify-between items-start inline-flex">
				<div
					className="px-[18px] py-2.5 bg-white rounded-lg border border-gray-200 justify-center items-center gap-2 flex cursor-pointer select-none"
					onClick={() => {
						onBack();
					}}
				>
					<div className="justify-start items-center gap-2 flex ">
						<div className="text-slate-700 text-base font-semibold font-['Hanken Grotesk'] leading-normal">Back</div>
					</div>
				</div>
				<div
					className={`h-11 px-[18px] py-2.5  rounded-lg shadow border  justify-center items-center gap-2 flex  select-none ${mappingToolStore.selectedLineItems.length === 0 ? 'bg-gray-200 text-gray-400' : 'text-white bg-blue-900 border-blue-900 cursor-pointer'}`}
					onClick={() => {
						if (mappingToolStore.selectedLineItems.length > 0) {
							onNext(2);
						}
					}}
				>
					<div className="justify-start items-center gap-2 flex">
						<div className=" text-base font-semibold font-['Hanken Grotesk'] leading-normal">Jump to Mapping</div>
					</div>
				</div>
				<div className="justify-end items-center gap-14 flex">
					<div
						className="h-11 px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex cursor-pointer select-none"
						onClick={async () => {
							await mappingToolStore.saveCurrentMapping('new');
						}}
					>
						<div className="justify-start items-center gap-2 flex">
							<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal ">Save Mapping</div>
						</div>
					</div>
				</div >
				<div className="justify-end items-center gap-14 flex">
					<div
						className="h-11 px-[18px] py-2.5 bg-red-500 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex cursor-pointer select-none"
						onClick={async () => {
							const yesNo = window.confirm('Are you sure you want save this to production?. It will overide any old data of this mappings');
							if (!yesNo) {
								return
							}
							let password = window.localStorage.getItem('tool_rebuild_password');
							if (!password) {
								password = window.prompt('Enter password to save to production');
							}
							if (password) {
								const mappingData = mappingToolStore.cloneCurrentMapping('verified', true) as any;
								delete mappingData.aiMatchings;
								const res = await RestAPI.fetchURL(`${RestAPI.SERVER_BASE_URL}/rest/estimates/setMappings`, 'POST', {
									password,
									mappingId: mappingToolStore.tupleID,
									mappingData
								});
								if (res) {
									if (res === 'Invalid-password.') {
										alert('Invalid password');
									}
									else {
										alert('Mapping NOT! saved to production. Reason: ' + res);
									}
								} else {
									window.localStorage.setItem('tool_rebuild_password', password);
									toastNotify('Mapping saved to production');
								}
							}
						}}
					>
						<div className="justify-start items-center gap-2 flex">
							<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal ">Save To Production</div>
						</div>
					</div>
				</div >
				<div className="justify-end items-center gap-14 flex">
					<div
						className="h-11 px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex cursor-pointer select-none"
						onClick={() => {
							onCancel();
						}}
					>
						<div className="justify-start items-center gap-2 flex">
							<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal ">Close</div>
						</div>
					</div>
				</div >
			</div >
		</div>

	</>
});

export default MappingWizardStep4;

function getChatInput(s: string): string {
	let userName = window.localStorage.getItem('lasso_chat_userName');
	while (!userName) {
		userName = window.prompt('Please enter you name');
		if (userName) {
			window.localStorage.setItem('lasso_chat_userName', userName);
		}
	}
	const now = new Date();

	const optionsDate = { month: 'short', day: '2-digit' };
	//@ts-ignore
	const formattedDate = now.toLocaleDateString('en-US', optionsDate).replace(' ', '/');

	const optionsTime = { hour: '2-digit', minute: '2-digit', hour12: false };
	//@ts-ignore
	const formattedTime = now.toLocaleTimeString('en-US', optionsTime);
	// const dateTime = new Date().toISOString();
	return `[${formattedDate} ${formattedTime}][${userName}] - ${s}`;
}

function removeUnwantedCharacters(cleanInput: string): string {
	const listOfUnwantedSeq = ['..', '...', "'", '.', ',', ':', '"', '^', '~', '`', ' '];
	if (listOfUnwantedSeq.includes(cleanInput)) return '';
	while (listOfUnwantedSeq.map(e => `${e} `).find(elm => cleanInput.startsWith(elm))) {
		cleanInput = cleanInput.substring(2);
	}
	if (listOfUnwantedSeq.includes(cleanInput)) return '';

	return cleanInput;
}

